import { z } from 'zod';
import { Division, ProjectModel, ReqStatus, VoucherScenario } from '../enums.js';
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data attesa nel formato YYYY-MM-DD');
const trim = z.string().trim();
const money = z.coerce.number().min(0).max(99_999_999);
const boolFromQuery = z.preprocess((v) => (typeof v === 'string' ? v === 'true' || v === '1' : v), z.boolean());
/**
 * Trattative (/api/v1/deals). Ogni trattativa vive in una **pipeline** (voucher,
 * rinnovi, estensioni, nuovi clienti) ed è ferma su uno **step** di quella
 * pipeline: pipeline e step sono dati, non enum, e si configurano nel seed
 * (`prisma/seed-pipelines.ts`).
 *
 * Il contatto può essere un cliente già in anagrafica (`clientId`) oppure solo
 * un nome con i suoi recapiti: i contatti del voucher arrivano da GoDeal e in
 * OS non sono ancora clienti.
 *
 * Sul valore ci sono due strade e non si mescolano:
 *  - pipeline VOUCHER: NON si digita niente. Si sceglie lo `scenario` (20k o
 *    40k) e il ricavo lo calcola il server (`ricavoStimato` nel DTO);
 *  - tutte le altre pipeline: vale `amount`.
 *
 * `probability` è facoltativa: senza, vale quella dello step. Passarla a mano la
 * "congela": da quel momento i cambi di step non la toccano più.
 */
export const createDealInput = z.object({
    title: trim.min(1).max(200),
    /** La pipeline: per id oppure per slug (uno dei due). Senza, vale la prima attiva. */
    pipelineId: z.string().optional(),
    pipeline: trim.max(60).optional(),
    /** Lo step di arrivo: per id oppure per nome. Senza, il primo step della pipeline. */
    stageId: z.string().optional(),
    stage: trim.max(80).optional(),
    clientId: z.string().optional().nullable(),
    contactName: trim.max(120).optional().nullable(),
    companyName: trim.max(160).optional().nullable(),
    email: trim.max(160).optional().nullable(),
    phone: trim.max(40).optional().nullable(),
    ghlContactId: trim.max(60).optional().nullable(),
    /** Solo fuori dal voucher: sul voucher il valore si calcola. */
    amount: money.default(0),
    scenario: z.nativeEnum(VoucherScenario).optional().nullable(),
    mesiCanone: z.coerce.number().int().min(0).max(120).optional(),
    reqConnettivita: z.nativeEnum(ReqStatus).optional(),
    reqPolizza: z.nativeEnum(ReqStatus).optional(),
    reqDurc: z.nativeEnum(ReqStatus).optional(),
    probability: z.coerce.number().int().min(0).max(100).optional(),
    expectedCloseDate: isoDate.optional().nullable(),
    fascia: trim.max(10).optional().nullable(),
    /**
     * Id del ricavo ricorrente che questa trattativa rinnova. Un rinnovo che si
     * prova a far passare dal voucher vive nella pipeline del voucher (gli step da
     * seguire sono quelli) e resta riconoscibile come rinnovo grazie a questo campo.
     */
    renewalOfId: z.string().optional().nullable(),
    ownerId: z.string().optional().nullable(),
    lostReason: trim.max(300).optional().nullable(),
    note: trim.max(5000).optional().nullable(),
});
/**
 * PATCH. Cambiando `pipelineId` serve anche uno `stageId` della pipeline nuova
 * (una trattativa voucher che non passa può diventare un'estensione).
 */
export const updateDealInput = createDealInput.partial().extend({
    order: z.coerce.number().optional(),
});
/**
 * Kanban: `position` = indice 0..N nella colonna di arrivo (escluso il deal
 * spostato), oppure `order` esplicito. Senza nessuno dei due va in fondo.
 * Chiudere da qui (uno step PERSO) richiede il motivo, come dal dettaglio.
 */
export const moveDealInput = z.object({
    stageId: z.string().min(1),
    position: z.coerce.number().int().min(0).optional(),
    order: z.coerce.number().optional(),
    lostReason: trim.max(300).optional().nullable(),
});
/** Filtro delle pill, calcolato sull'esito dello step: in corso, vinte, chiuse, tutte. */
export const DealView = { APERTE: 'aperte', VINTE: 'vinte', CHIUSE: 'chiuse', TUTTE: 'tutte' };
export const dealsListQuery = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(500).default(50),
    view: z.nativeEnum(DealView).default(DealView.APERTE),
    /** Slug della pipeline; senza, si guardano tutte. */
    pipeline: trim.max(60).optional(),
    stageId: z.string().optional(),
    ownerId: z.string().optional(),
    fascia: trim.max(10).optional(),
    search: trim.optional(),
    /** Finestra su expectedCloseDate (la stat-band dei periodi filtra da qui). */
    from: isoDate.optional(),
    to: isoDate.optional(),
    /** Rimette in lista anche le chiuse quando si guardano le aperte. */
    includeLost: boolFromQuery.optional(),
    /** Solo quelle ferme da oltre DEAL_STALE_DAYS giorni. */
    stale: boolFromQuery.optional(),
});
export const dealsSummaryQuery = z.object({ pipeline: trim.max(60).optional() });
/** Nota o contatto scritti a mano nello storico (i cambi di step li scrive il server). */
export const dealEventInput = z.object({
    kind: z.enum(['NOTA', 'CONTATTO']).default('NOTA'),
    text: trim.min(1).max(2000),
});
/** Prossima azione: crea un Task collegato alla trattativa. */
export const dealNextActionInput = z.object({
    title: trim.min(1).max(200),
    dueDate: isoDate.optional().nullable(),
    assigneeId: z.string().optional().nullable(),
});
/**
 * Import massivo, idempotente su `ghlContactId`: una riga già importata viene
 * aggiornata, non duplicata. Le righe senza `ghlContactId` vengono sempre
 * create. `title` è facoltativo: senza, vale l'azienda (o il nome del contatto).
 */
export const dealImportRow = createDealInput.partial({ title: true }).extend({
    title: trim.min(1).max(200).optional(),
});
export const dealImportInput = z.object({
    /** Pipeline di destinazione per le righe che non la indicano (slug). */
    pipeline: trim.max(60).optional(),
    deals: z.array(dealImportRow).min(1).max(1000),
    /** false = le righe già presenti si contano come saltate e non si toccano. */
    updateExisting: z.boolean().default(true),
});
// ---------------------------------------------------------------------------
// Dalla trattativa vinta al progetto
// ---------------------------------------------------------------------------
/**
 * `POST /deals/:id/project` — apre il progetto nato da una trattativa vinta.
 *
 * Il server **propone** (vedi `GET /deals/:id/project-proposal`), qui arriva
 * quello che l'utente ha confermato. Sono i soli quattro campi che servono per
 * aprire: chi, come si chiama, cosa si vende, quando si consegna. Tutto il
 * resto (team, costi, milestone) si compila dopo, dalla scheda del progetto.
 *
 * `revenue` non ha un default inventato: senza, vale l'imponibile che la
 * trattativa aveva già scritto.
 */
export const dealToProjectInput = z.object({
    name: trim.min(1).max(200),
    /**
     * Il cliente in anagrafica. Facoltativo solo perché una trattativa può
     * ancora non averne uno (i contatti del voucher arrivano da GoDeal): in quel
     * caso il server rifiuta e chiede di collegare prima l'anagrafica, perché un
     * progetto senza cliente non si può fatturare.
     */
    clientId: z.string().optional().nullable(),
    division: z.nativeEnum(Division),
    model: z.nativeEnum(ProjectModel),
    deadline: isoDate.optional().nullable(),
    startDate: isoDate.optional().nullable(),
    /** Quanto è stato venduto. Senza, vale il valore già scritto sulla trattativa. */
    revenue: money.optional(),
    /**
     * Voucher: insieme al progetto nasce anche la riga di canone ricorrente della
     * gestione pratica (120 €/mese per i mesi a preventivo). Sono due cose
     * diverse — l'imponibile del progetto e un servizio venduto sopra — e
     * dimenticare la seconda vuol dire perdere la gestione pratica per strada.
     */
    creaCanone: z.boolean().default(true),
});
/**
 * `POST /deals/:id/renew` — la vittoria di un RINNOVO non apre un progetto
 * nuovo: prolunga il canone che c'è già. Sono gli stessi soldi, e aprire una
 * commessa nuova li conterebbe due volte.
 *
 * La nuova scadenza si scrive, non si indovina: il server non mette default.
 */
export const dealRenewInput = z.object({
    /** Fino a quando vale il contratto rinnovato. */
    endDate: isoDate,
    /** Il nuovo importo, se il rinnovo cambia il prezzo. Senza, resta quello di oggi. */
    amountNet: money.optional(),
    /** Da quando parte il nuovo prezzo. Senza, dal giorno dopo la vecchia scadenza. */
    startDate: isoDate.optional().nullable(),
});
//# sourceMappingURL=deal.js.map